import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmlFlowEnum } from '@/api';
import {
  ConnectionChart,
  COUNTERPARTY_SIGNAL_CHART_COLORS,
  type CounterpartySignalChartCategory,
} from '../ConnectionChart';
import { getRiskGroups, getSignalTranslation } from '@/helpers';
import { capitalize } from 'lodash';

import styles from './CounterpartySignals.module.sass';

interface CounterpartySignalsProps {
  counterparty: Record<string, any>;
  flow: AmlFlowEnum;
}

const CounterpartySignals = ({
  counterparty,
  flow,
}: CounterpartySignalsProps) => {
  const { t } = useTranslation();

  const cpInGroups = useMemo(
    () =>
      counterparty?.signals?.in
        ? getRiskGroups(counterparty?.signals?.in, flow)
        : {},
    [counterparty, flow],
  );

  const receivedData = useMemo(() => {
    const labels: string[] = [];
    const datasets: number[] = [];
    const backgroundColors: string[] = [];
    const segmentRiskKeys: CounterpartySignalChartCategory[] = [];

    Object.keys(cpInGroups).forEach((category) => {
      const cat = category as CounterpartySignalChartCategory;
      cpInGroups[category].forEach((signal: Record<string, any>) => {
        backgroundColors.push(COUNTERPARTY_SIGNAL_CHART_COLORS[cat]);
        segmentRiskKeys.push(cat);
        labels.push(
          `${signal.value}% ${capitalize(getSignalTranslation(signal.label))}`,
        );
        datasets.push(signal.value);
      });
    });

    return {
      labels,
      datasets: [
        {
          data: datasets,
          backgroundColor: backgroundColors,
          segmentRiskKeys,
        },
      ],
    };
  }, [cpInGroups]);

  const cpOutGroups = useMemo(
    () =>
      counterparty?.signals?.out
        ? getRiskGroups(counterparty?.signals?.out, flow)
        : {},
    [counterparty, flow],
  );

  const sentData = useMemo(() => {
    const labels: string[] = [];
    const datasets: number[] = [];
    const backgroundColors: string[] = [];
    const segmentRiskKeys: CounterpartySignalChartCategory[] = [];

    Object.keys(cpOutGroups).forEach((category) => {
      const cat = category as CounterpartySignalChartCategory;
      cpOutGroups[category].forEach((signal: Record<string, any>) => {
        backgroundColors.push(COUNTERPARTY_SIGNAL_CHART_COLORS[cat]);
        segmentRiskKeys.push(cat);
        labels.push(
          `${signal.value}% ${capitalize(getSignalTranslation(signal.label))}`,
        );
        datasets.push(signal.value);
      });
    });

    return {
      labels,
      datasets: [
        {
          data: datasets,
          backgroundColor: backgroundColors,
          segmentRiskKeys,
        },
      ],
    };
  }, [cpOutGroups]);

  return (
    <div className={styles.container}>
      <div className={styles.chartColumn}>
        <ConnectionChart
          chartData={receivedData}
          centerAmount={counterparty?.received_fiat_amount || null}
          centerTitle={t('translation.connections_received_title')}
          flowDirection="received"
        />
      </div>
      <div className={styles.divider} aria-hidden />
      <div className={styles.chartColumn}>
        <ConnectionChart
          chartData={sentData}
          centerAmount={counterparty?.sent_fiat_amount || null}
          centerTitle={t('translation.connections_sent_title')}
          flowDirection="sent"
        />
      </div>
    </div>
  );
};

export default CounterpartySignals;
